import React, { useEffect, useState } from 'react';
import { useListingReels } from 'hooks/useListingReels';
import ReelPlayer from 'components/reels/ReelPlayer';

export interface ListingReelsSectionProps {
  listingId: string;
}

/**
 * Feature 009 (US3): renders a gallery of reels associated with a listing.
 * Returns {@code null} when the listing has no active reels so the section is invisible
 * in that case (spec FR-017 / SC-005). Clicking a thumbnail opens an in-page modal
 * that plays the reel with audio (user-initiated, compatible with browser autoplay policy).
 */
const ListingReelsSection: React.FC<ListingReelsSectionProps> = ({ listingId }) => {
  const { reels, loading, error } = useListingReels(listingId);
  const [openReelId, setOpenReelId] = useState<string | null>(null);

  // Lock background scroll while the modal is open.
  useEffect(() => {
    if (!openReelId) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [openReelId]);

  // Close on Escape.
  useEffect(() => {
    if (!openReelId) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenReelId(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [openReelId]);

  if (loading) {
    return (
      <section className="my-4" aria-busy="true">
        <h5 className="mb-3">İlan Reelleri</h5>
        <div className="text-muted small">
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          Reeller yükleniyor...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="my-4">
        <h5 className="mb-3">İlan Reelleri</h5>
        <div className="alert alert-warning py-2 small mb-0">{error}</div>
      </section>
    );
  }

  if (reels.length === 0) {
    return null;
  }

  const activeReel = reels.find((r) => r.id === openReelId) ?? null;

  return (
    <section className="my-4">
      <h5 className="mb-3">İlan Reelleri</h5>

      <div className="d-flex gap-3 overflow-auto pb-2">
        {reels.map((reel) => (
          <button
            key={reel.id}
            type="button"
            className="card border-0 shadow-sm p-0 flex-shrink-0"
            style={{
              width: 140,
              height: 220,
              background: 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => setOpenReelId(reel.id)}
            aria-label="Reel'i oynat"
          >
            <video
              src={reel.videoUrl}
              muted
              preload="metadata"
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '0.5rem',
                display: 'block',
              }}
            />
          </button>
        ))}
      </div>

      {activeReel && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenReelId(null);
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0">
                <button
                  type="button"
                  className="btn-close btn-close-white ms-auto"
                  aria-label="Kapat"
                  onClick={() => setOpenReelId(null)}
                />
              </div>
              <div
                className="modal-body p-0 d-flex justify-content-center"
                style={{ minHeight: '60vh' }}
              >
                <div
                  style={{
                    width: 'min(90vw, 420px)',
                    height: '70vh',
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    background: '#000',
                  }}
                >
                  <ReelPlayer
                    videoUrl={activeReel.videoUrl}
                    isActive
                    playWithAudio
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ListingReelsSection;
